import React, { useState, useEffect } from 'react';
import { videoAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Trash2, Youtube, ExternalLink, Loader2 } from 'lucide-react';

const AdminVideoDashboard = ({ onClose }) => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [url, setUrl] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const loadVideos = async () => {
        try {
            setLoading(true);
            const response = await videoAPI.getAllVideos();
            setVideos(response.data);
            setError(null);
        } catch (err) {
            console.error('Failed to load videos:', err);
            setError('Failed to load videos.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadVideos();
    }, []);

    const handleAddVideo = async (e) => {
        e.preventDefault();
        if (!url) return;

        try {
            setSubmitting(true);
            setError(null);
            await videoAPI.addVideo(url, title, description);
            setUrl('');
            setTitle('');
            setDescription('');
            loadVideos();
        } catch (err) {
            console.error('Failed to add video:', err);
            setError(err.response?.data?.message || 'Failed to add video. Check URL.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Delete this video?')) {
            try {
                await videoAPI.deleteVideo(id);
                loadVideos();
            } catch (err) {
                console.error('Failed to delete video:', err);
                setError('Failed to delete video.');
            }
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-300">
            <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
                <CardHeader className="flex flex-row items-center justify-between border-b bg-muted/40 px-6 py-4">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-red-100 rounded-full text-red-600">
                            <Youtube className="h-5 w-5" />
                        </div>
                        <CardTitle className="text-xl">Manage YouTube Gallery</CardTitle>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
                </CardHeader>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Add Video Form */}
                    <Card className="bg-muted/30 border-dashed">
                        <CardContent className="pt-6">
                            <form onSubmit={handleAddVideo} className="flex flex-col md:flex-row gap-4 items-end">
                                <div className="flex-1 w-full space-y-2">
                                    <label className="text-sm font-medium">YouTube URL</label>
                                    <Input
                                        placeholder="https://www.youtube.com/watch?v=..."
                                        value={url}
                                        onChange={(e) => setUrl(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="flex-1 w-full space-y-2">
                                    <label className="text-sm font-medium">Title (Optional)</label>
                                    <Input
                                        placeholder="Video Title"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                    />
                                </div>
                                <div className="flex-1 w-full space-y-2">
                                    <label className="text-sm font-medium">Description (Optional)</label>
                                    <textarea
                                        className="flex min-h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                                        placeholder="Short description..."
                                        rows={1}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    />
                                </div>
                                <Button type="submit" disabled={submitting} className="min-w-[100px] mb-0.5">
                                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Video'}
                                </Button>
                            </form>
                            {error && (
                                <p className="text-sm text-destructive mt-3 flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 rounded-full bg-destructive"></span>
                                    {error}
                                </p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Video List */}
                    <div className="rounded-md border overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Thumbnail</TableHead>
                                    <TableHead>Title / URL</TableHead>
                                    <TableHead>Added On</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={4} className="h-24 text-center">
                                            <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                                        </TableCell>
                                    </TableRow>
                                ) : videos.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                                            No videos found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    videos.map((vid) => (
                                        <TableRow key={vid.id}>
                                            <TableCell className="w-[120px]">
                                                {/* YouTube Thumbnail URL format: https://img.youtube.com/vi/<insert-youtube-video-id-here>/0.jpg */}
                                                <div className="relative aspect-video rounded-md overflow-hidden bg-muted">
                                                    <img
                                                        src={`https://img.youtube.com/vi/${vid.embedId}/mqdefault.jpg`}
                                                        alt="thumbnail"
                                                        className="object-cover w-full h-full"
                                                        onError={(e) => e.currentTarget.style.display = 'none'}
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="font-medium">{vid.title || 'Untitled Video'}</div>
                                                <a
                                                    href={vid.videoUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-xs text-blue-500 hover:underline flex items-center gap-1 mt-1"
                                                >
                                                    {vid.videoUrl} <ExternalLink className="h-3 w-3" />
                                                </a>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground text-sm">
                                                {vid.createdAt ? new Date(vid.createdAt).toLocaleDateString() : '-'}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-destructive hover:bg-destructive/10"
                                                    onClick={() => handleDelete(vid.id)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default AdminVideoDashboard;
